import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './LoginPage.module.css'

function EyeIcon({ open }) {
  return open ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  )
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000'

export default function LoginPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [session, setSession] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (session) {
        // NEW_PASSWORD_REQUIRED challenge
        const res = await fetch(`${API_URL}/api/auth/new-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, newPassword, session }),
        })
        const data = await res.json()
        if (!res.ok) {
          setError(data.error || 'Password change failed')
          if (res.status === 401) setSession(null) // session expired, restart
          setLoading(false)
          return
        }
        storeTokensAndRedirect(data)
      } else {
        // Initial login
        const res = await fetch(`${API_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        })
        const data = await res.json()
        if (!res.ok) {
          if (res.status === 403 && data.error === 'NEW_PASSWORD_REQUIRED') {
            setSession(data.session)
            setLoading(false)
            return
          }
          setError(data.error || 'Login failed')
          setLoading(false)
          return
        }
        storeTokensAndRedirect(data)
      }
    } catch {
      setError('Network error — please try again')
      setLoading(false)
    }
  }

  function storeTokensAndRedirect(data) {
    localStorage.setItem('sd_access_token', data.accessToken)
    localStorage.setItem('sd_id_token', data.idToken)
    localStorage.setItem('sd_refresh_token', data.refreshToken)
    navigate('/dashboard', { replace: true })
    window.location.reload()
  }

  const requireNewPassword = session !== null

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <span className={styles.logoIcon}>◈</span>
          <span className={styles.logoName}>Structura</span>
        </div>
        <h1 className={styles.heading}>
          {requireNewPassword ? 'Set your password' : 'Sign in'}
        </h1>
        {requireNewPassword && (
          <p className={styles.hint}>
            This is your first sign-in. Choose a new password to continue.
          </p>
        )}
        <form onSubmit={handleSubmit} className={styles.form}>
          {!requireNewPassword && (
            <>
              <label className={styles.label}>
                Email
                <input
                  className={styles.input}
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </label>
              <label className={styles.label}>
                Password
                <div className={styles.passwordWrap}>
                  <input
                    className={styles.input}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    disabled={loading}
                  />
                  <button type="button" className={styles.eyeBtn} onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
                    <EyeIcon open={showPassword} />
                  </button>
                </div>
              </label>
            </>
          )}
          {requireNewPassword && (
            <label className={styles.label}>
              New password
              <div className={styles.passwordWrap}>
                <input
                  className={styles.input}
                  type={showNewPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                  minLength={8}
                />
                <button type="button" className={styles.eyeBtn} onClick={() => setShowNewPassword(v => !v)} tabIndex={-1}>
                  <EyeIcon open={showNewPassword} />
                </button>
              </div>
            </label>
          )}
          {error && <p className={styles.error}>{error}</p>}
          <button className={styles.btn} type="submit" disabled={loading}>
            {loading
              ? 'Signing in…'
              : requireNewPassword
                ? 'Set password & sign in'
                : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  )
}
